import cx from 'classnames';
import invariant from 'invariant';
import { camelCase } from 'lodash';
import * as React from 'react';
import ReactDOM from 'react-dom';

import { FocusEventHandler, MouseEventHandler } from 'types/typescript/eventHandlers';

import createChainedFunction from 'utils/createChainedFunction';

import './styles/Text.scss';

// # TODO
// * Enforce `focus`-able element with `on*` props

export type TextAlign = 'inherit' | 'left' | 'center' | 'right';

export type TextElement =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'div'
  | 'figcaption'
  | 'figure'
  | 'header'
  | 'section'
  | 'p'
  | 'span'
  | 'code';

export type TextSize =
  | 'xxxxlarge'
  | 'xxxlarge'
  | 'xxlarge'
  | 'xlarge'
  | 'large'
  | 'medium'
  | 'small'
  | 'xsmall'
  | 'xxsmall'
  | 'xxxsmall'
  | 'xxxxsmall'
  | 'xxxxxsmall';

export type TextColor =
  | 'inherit'
  | 'lava'
  | 'flame'
  | 'sunrise'
  | 'jungle'
  | 'shamrock'
  | 'midnight'
  | 'brand'
  | 'violet'
  | 'pitch'
  | 'thunder'
  | 'eclipse'
  | 'koala'
  | 'wolf'
  | 'white';

export type CoachingTextColor = 'ink' | 'koolaid';

export type TextFont = 'inherit' | 'sans-serif' | 'serif';

export type TextWeight = 'bold' | 'semibold' | 'normal' | 'light';

export type TextWrap = boolean | 'force';

export type OptionalProps = {
  alignWithIcon?: boolean;
  capitalize?: boolean;
  children: any;
  className?: string;
  color?: TextColor | CoachingTextColor;
  decoration?: 'dashes';
  flush?: boolean;
  italic?: boolean;
  lineHeight?: 'xlarge' | 'large' | 'medium' | 'flush';
  size?: TextSize;
  smallCaps?: boolean;
  truncate?: boolean;
  uppercase?: boolean;
  weight?: TextWeight;
  onBlur?: FocusEventHandler;
  onClick?: MouseEventHandler;
  onFocus?: FocusEventHandler;
  onMouseEnter?: MouseEventHandler;
  onMouseLeave?: MouseEventHandler;
  onMouseOver?: MouseEventHandler;
  'data-test-selector'?: string;
};

type DefaultProps = {
  align: TextAlign;
  element: TextElement;
  font: TextFont;
  wrap: TextWrap;
};

export type Props = OptionalProps & DefaultProps;

type State = {
  label: string;
};

export default class Text extends React.Component<Props, State> {
  static defaultProps: DefaultProps = {
    align: 'inherit',
    element: 'span',
    font: 'inherit',
    wrap: true,
  };

  state = {
    label: '',
  };

  handleMouseOver = () => {
    if (!this.props.truncate) {
      return;
    }

    const container = ReactDOM.findDOMNode(this);
    invariant(container instanceof HTMLElement, 'Text findDOMNode returned non HTMLElement');

    const textWidth = container
      ? (container.childNodes[0] as Element).getBoundingClientRect().width
      : 0;
    const visibleWidth = container ? (container as Element).getBoundingClientRect().width : 0;

    if (container && textWidth > visibleWidth) {
      this.setState({ label: container.textContent || '' });
    } else {
      this.setState({ label: '' });
    }
  };

  render() {
    const {
      align,
      alignWithIcon,
      children,
      capitalize,
      className,
      color,
      decoration,
      element,
      flush,
      font,
      italic,
      lineHeight,
      onClick,
      onMouseOver,
      onFocus,
      onBlur,
      onMouseEnter,
      onMouseLeave,
      size,
      smallCaps,
      truncate,
      uppercase,
      weight,
      wrap,
    } = this.props;
    const { label } = this.state;

    const Component = element;

    const body = truncate ? <span>{children}</span> : children;

    return (
      <Component
        title={label}
        className={cx(
          'Text',
          align && `Text--align--${align}`,
          alignWithIcon && 'Text--alignWithIcon',
          capitalize && 'Text--capitalize',
          color && `Text--${color}`,
          decoration && `Text--${decoration}`,
          flush && 'Text--flush',
          font && `Text--font--${camelCase(font)}`,
          italic && 'Text--italic',
          lineHeight && `Text--line-height--${lineHeight}`,
          size && `Text--${size}`,
          smallCaps && 'Text--smallCaps',
          truncate && 'Text--truncate',
          uppercase && 'Text--uppercase',
          weight && `Text--${weight}`,
          wrap === 'force' && 'Text--wrap',
          !wrap && 'Text--noWrap',
          className
        )}
        onBlur={onBlur}
        onClick={onClick}
        onFocus={onFocus}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        // createChainedFunction sometimes returns `null`, but we just return undefined
        // in order to make Typescript happy, since onMouseOver does not expect to be `null`
        onMouseOver={createChainedFunction(this.handleMouseOver, onMouseOver) || undefined}
        data-test-selector={this.props['data-test-selector']}>
        {body}
      </Component>
    );
  }
}
